import { ListFilters } from "@dashboard/components/AppLayout/ListFilters";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TablePagination, { ListSettingsUpdate } from "@dashboard/components/TablePagination";
import TableRowLink from "@dashboard/components/TableRowLink";
import { WsmFeeBasis, WsmFeeFragment } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { renderCollection } from "@dashboard/misc";
import { ListSettings } from "@dashboard/types";
import { WsmAmount } from "@dashboard/wsmShared/money";
import { TableBody, TableCell, TableFooter, TableHead } from "@material-ui/core";
import { Box, Button, Skeleton } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { getFeeScopeLabel } from "../../labels";
import { feeMessages, sectionMessages } from "../../messages";
import { wsmFeeAddPath, wsmFeeUrl } from "../../urls";
import WsmOneLine from "../WsmOneLine/WsmOneLine";
import WsmYesNo from "../WsmYesNo/WsmYesNo";

const numberOfColumns = 6;

export interface WsmFeeListPageProps {
  fees: WsmFeeFragment[] | undefined;
  query: string;
  settings: ListSettings;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onQueryChange: (query: string) => void;
  onUpdateListSettings: ListSettingsUpdate;
  onNextPage: () => void;
  onPreviousPage: () => void;
}

export const WsmFeeListPage = ({
  fees,
  query,
  settings,
  hasNextPage,
  hasPreviousPage,
  onQueryChange,
  onUpdateListSettings,
  onNextPage,
  onPreviousPage,
}: WsmFeeListPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();

  return (
    <ListPageLayout>
      <TopNav title={intl.formatMessage(sectionMessages.feeListTitle)}>
        <Box display="flex" alignItems="center" gap={3}>
          <Button onClick={() => navigate(wsmFeeAddPath)} data-test-id="wsm-fee-list-create">
            {intl.formatMessage(feeMessages.create)}
          </Button>
        </Box>
      </TopNav>
      <DashboardCard data-test-id="wsm-fee-list">
        <DashboardCard.Content paddingX={0}>
          <Box paddingTop={4} paddingBottom={2}>
            <ListFilters
              type="expression-filter"
              hideFilters
              initialSearch={query}
              searchPlaceholder={intl.formatMessage(sectionMessages.searchPlaceholder)}
              onSearchChange={onQueryChange}
            />
          </Box>
          <ResponsiveTable>
            <TableHead>
              <TableRowLink>
                <TableCell>{intl.formatMessage(feeMessages.colLabel)}</TableCell>
                <TableCell>{intl.formatMessage(feeMessages.colProduct)}</TableCell>
                <TableCell>{intl.formatMessage(feeMessages.colSku)}</TableCell>
                <TableCell>{intl.formatMessage(feeMessages.colAmount)}</TableCell>
                <TableCell>{intl.formatMessage(feeMessages.colApplyTo)}</TableCell>
                <TableCell>{intl.formatMessage(feeMessages.colRequired)}</TableCell>
              </TableRowLink>
            </TableHead>
            <TableBody>
              {renderCollection(
                fees,
                fee => (
                  <TableRowLink
                    key={fee ? fee.id : "skeleton"}
                    hover={!!fee}
                    href={fee ? wsmFeeUrl(fee.id) : undefined}
                    data-test-id="wsm-fee-row"
                  >
                    <TableCell>{fee ? <WsmOneLine>{fee.label}</WsmOneLine> : <Skeleton />}</TableCell>
                    <TableCell>
                      {fee ? <WsmOneLine>{fee.product.name}</WsmOneLine> : <Skeleton />}
                    </TableCell>
                    <TableCell>{fee ? fee.sku || "-" : <Skeleton />}</TableCell>
                    {/* Every amount in the section goes through one renderer, so
                        a percentage cannot read as dollars on the row below. */}
                    <TableCell style={{ whiteSpace: "nowrap" }} data-test-id="wsm-fee-amount">
                      {fee ? (
                        <WsmAmount
                          amount={fee.amount}
                          currency={fee.currencyCode}
                          basis={fee.basis === WsmFeeBasis.PERCENT ? "percent" : "fixed"}
                        />
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>
                      {fee ? intl.formatMessage(getFeeScopeLabel(fee.applyTo)) : <Skeleton />}
                    </TableCell>
                    <TableCell>{fee ? <WsmYesNo value={fee.required} /> : <Skeleton />}</TableCell>
                  </TableRowLink>
                ),
                () => (
                  <TableRowLink data-test-id="wsm-fee-list-empty">
                    <TableCell colSpan={numberOfColumns}>
                      <Box paddingY={6}>{intl.formatMessage(feeMessages.noFees)}</Box>
                    </TableCell>
                  </TableRowLink>
                ),
              )}
            </TableBody>
            <TableFooter>
              <TableRowLink>
                <TablePagination
                  colSpan={numberOfColumns}
                  settings={settings}
                  onUpdateListSettings={onUpdateListSettings}
                  hasNextPage={hasNextPage}
                  hasPreviousPage={hasPreviousPage}
                  onNextPage={onNextPage}
                  onPreviousPage={onPreviousPage}
                />
              </TableRowLink>
            </TableFooter>
          </ResponsiveTable>
        </DashboardCard.Content>
      </DashboardCard>
    </ListPageLayout>
  );
};

export default WsmFeeListPage;
